/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * WelcomeScreen — the Build 130 welcome carousel, extracted verbatim.
 *
 * This is the exact welcome half of the old two-screen AuthScreen.tsx that
 * shipped in Build 130 (commit 26a92a1), lifted into a standalone full-screen
 * component. The carousel markup, slides, copy, dot indicators, drag/keyboard
 * handlers, auto-advance, and styling are byte-for-byte the same — the only
 * change is that the Apple/Google/email "Screen 2" was dropped (phone-only
 * now) and the two CTAs both lead to phone entry via `onGetStarted`. The
 * `view`-driven slide-off animation wrapper is gone since there is only one
 * screen here; the carousel's own auto-advance no longer needs the
 * `view === "welcome"` guard.
 */

import {
  useCallback,
  useEffect,
  useId,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { motion, useReducedMotion, type PanInfo } from "framer-motion";
import { MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import slide1 from "@/assets/onboarding/slide1.jpg";
import slide2 from "@/assets/onboarding/slide2.jpg";
import slide3 from "@/assets/onboarding/slide3.jpg";

const CAROUSEL_INTERVAL_MS = 4000;
const SLIDE_COUNT = 3;
// Drag distance (px) past which the carousel commits to a slide change.
const SWIPE_THRESHOLD = 40;

const SLIDES = [
  { image: slide1, titleKey: "auth.slide1Title", subtitleKey: "auth.slide1Subtitle", altKey: "auth.slide1Alt" },
  { image: slide2, titleKey: "auth.slide2Title", subtitleKey: "auth.slide2Subtitle", altKey: "auth.slide2Alt" },
  { image: slide3, titleKey: "auth.slide3Title", subtitleKey: "auth.slide3Subtitle", altKey: "auth.slide3Alt" },
] as const;

interface Props {
  /**
   * Advance to phone entry. Both the "Get started" CTA and the "Log in" link
   * call this — phone-only has a single sign-in entry, so there is no separate
   * login route (Firebase Auth creates the account on first verification or
   * returns the existing one).
   */
  onGetStarted: () => void;
}

export default function WelcomeScreen({ onGetStarted }: Props) {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  // Carousel state. `tickKey` bumps on any manual interaction (drag, dot,
  // arrow key) which re-fires the auto-advance effect and resets the
  // interval — so a tap-then-wait shows ~4s before the next auto move,
  // not "0.1s left in the previous tick fires immediately."
  const [slideIndex, setSlideIndex] = useState(0);
  const [tickKey, setTickKey] = useState(0);
  const [autoPaused, setAutoPaused] = useState(false);

  // Auto-advance. Disabled when the user prefers reduced motion or when the
  // user has manually paused via drag.
  useEffect(() => {
    if (prefersReducedMotion) return;
    if (autoPaused) return;
    const id = setInterval(() => {
      setSlideIndex((i) => (i + 1) % SLIDE_COUNT);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [prefersReducedMotion, autoPaused, tickKey]);

  const goToSlide = useCallback((next: number) => {
    setSlideIndex(((next % SLIDE_COUNT) + SLIDE_COUNT) % SLIDE_COUNT);
    setTickKey((k) => k + 1);
  }, []);

  const onSlideDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      const dx = info.offset.x;
      if (dx <= -SWIPE_THRESHOLD) {
        goToSlide(slideIndex + 1);
      } else if (dx >= SWIPE_THRESHOLD) {
        goToSlide(slideIndex - 1);
      } else {
        // Snap back without changing slide.
        setTickKey((k) => k + 1);
      }
      setAutoPaused(false);
    },
    [goToSlide, slideIndex],
  );

  const onCarouselKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goToSlide(slideIndex + 1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToSlide(slideIndex - 1);
      }
    },
    [goToSlide, slideIndex],
  );

  const carouselLiveId = useId();
  const currentSlide = SLIDES[slideIndex];

  return (
    // Bug 2 fix — the root must be a FLEX container with a DEFINITE height
    // (h-screen, not just min-h-screen) so the <section> below becomes a
    // stretched flex child with a real resolved height. The carousel's images
    // are `absolute inset-0 h-full`, and `height:100%` only resolves against a
    // parent with a definite height — not one that only has `min-height`. In
    // Build 130 the welcome <section> was a `w-1/2` child of a `flex
    // min-h-screen` row, so flex `align-items:stretch` gave it that definite
    // height; the 2A recovery dropped that flex-row wrapper, collapsing the
    // chain to min-heights only, which made the slide images render at 0px
    // (blank) inside the native WKWebView. Restoring `h-screen flex flex-col`
    // here re-establishes the definite-height cascade without changing the
    // visual layout.
    <div
      className="h-screen flex flex-col bg-[#0d0c1d] text-white overflow-x-hidden"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <section
        data-testid="welcome-hero"
        aria-label={t("auth.carouselLabel")}
        className="flex-1 min-h-0 flex flex-col px-7 pt-14 pb-7"
        style={{
          background:
            "radial-gradient(420px 300px at 50% 2%, rgba(150,110,255,0.18), transparent 70%), radial-gradient(380px 320px at 88% 98%, rgba(255,110,170,0.10), transparent 70%), #131127",
        }}
      >
        <header className="flex items-center justify-center gap-2.5 mb-5">
          <div className="w-10 h-10 rounded-xl gradient-bg flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(150,90,230,0.6)]">
            <MapPin size={20} className="text-white" aria-hidden="true" />
          </div>
          <p
            data-testid="welcome-wordmark"
            className="text-base font-semibold tracking-wide text-white"
          >
            MyAmbits
          </p>
        </header>

        <div
          className="flex-1 flex flex-col min-h-0"
          role="region"
          aria-roledescription="carousel"
          aria-label={t("auth.carouselLabel")}
          tabIndex={0}
          onKeyDown={onCarouselKeyDown}
        >
          <div className="relative flex-1 overflow-hidden rounded-3xl bg-[#211f3c] min-h-[360px]">
            <motion.div
              className="flex h-full w-full"
              animate={{ x: `-${slideIndex * 100}%` }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { type: "tween", duration: 0.42, ease: [0.4, 0, 0.2, 1] }
              }
              drag={prefersReducedMotion ? false : "x"}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.18}
              onDragStart={() => setAutoPaused(true)}
              onDragEnd={onSlideDragEnd}
            >
              {SLIDES.map((slide, i) => {
                const isActive = i === slideIndex;
                return (
                  <div
                    key={slide.titleKey}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={t("auth.slideOf", {
                      n: i + 1,
                      total: SLIDE_COUNT,
                    })}
                    aria-hidden={!isActive}
                    className="relative h-full min-w-full"
                  >
                    <img
                      src={slide.image}
                      alt={t(slide.altKey)}
                      className="absolute inset-0 h-full w-full object-cover select-none"
                      draggable={false}
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0"
                      style={{ background: "rgba(92,60,170,0.20)", mixBlendMode: "soft-light" }}
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(13,12,29,0.97) 0%, rgba(13,12,29,0.80) 24%, rgba(13,12,29,0.34) 48%, rgba(13,12,29,0.08) 70%, rgba(13,12,29,0.20) 100%)",
                      }}
                    />
                    <div className="absolute inset-x-0 bottom-0 p-7">
                      <h2 className="text-[28px] font-bold tracking-tight leading-[1.12] text-white mb-2.5 whitespace-pre-line">
                        {t(slide.titleKey)}
                      </h2>
                      <p className="text-[15.5px] leading-[1.5] text-[#e2e0ef]">
                        {t(slide.subtitleKey)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>

          <div
            id={carouselLiveId}
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          >
            {t("auth.slideOf", { n: slideIndex + 1, total: SLIDE_COUNT })}:{" "}
            {t(currentSlide.titleKey)}
          </div>

          <div className="flex items-center justify-center gap-2 mt-5" role="tablist" aria-label={t("auth.carouselLabel")}>
            {SLIDES.map((slide, i) => {
              const isActive = i === slideIndex;
              return (
                <button
                  key={slide.titleKey}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={carouselLiveId}
                  aria-label={t("auth.goToSlide", {
                    n: i + 1,
                    total: SLIDE_COUNT,
                  })}
                  onClick={() => goToSlide(i)}
                  className={`h-[7px] rounded-full transition-all ${
                    isActive
                      ? "w-[22px] bg-[#9b78ff]"
                      : "w-[7px] bg-[#322f55] hover:bg-[#4a467a]"
                  }`}
                />
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-6">
          <button
            type="button"
            onClick={onGetStarted}
            className="gradient-bg text-white h-[55px] rounded-2xl font-semibold text-base shadow-[0_14px_30px_-10px_rgba(150,90,230,0.7)] active:scale-[0.98] transition-transform"
          >
            {t("auth.getStarted")}
          </button>
          <button
            type="button"
            onClick={onGetStarted}
            className="text-center text-[15.5px] text-[#a3a1bf] py-1"
          >
            {t("auth.alreadyHaveAccount")}{" "}
            <span className="text-[#9b78ff] font-semibold">
              {t("auth.logIn")}
            </span>
          </button>
        </div>
      </section>
    </div>
  );
}
